import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import Select from 'react-select';
import SettingsPageLayout from './SettingsPageLayout';
import toast from '../shared/Toast';
import {
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from '../../_services/notifications.service';
import { getNotificationRole } from '../notifications/role';

/**
 * Settings → Notifications.
 *
 * One page, three concerns:
 *   1. What reaches ME, per event type (in-app mode + email toggle).
 *   2. Managers: whose activity reaches me (employed / self-employed staff).
 *   3. Self-employed: whether my activity is shared with the salon manager.
 *
 * Everything autosaves on change through PATCH /notification-v2/preferences.
 * In-app modes not explicitly overridden render from resolvedTypes — the
 * server-computed effective default for this user's role and salon size.
 */

const MODE_OPTIONS = [
  { value: 'realtime', label: 'Real-time' },
  { value: 'digest', label: 'Daily digest' },
  { value: 'off', label: 'Off' },
];

const TYPE_GROUPS = [
  {
    title: 'Bookings',
    hint: 'New bookings cover every flow: instant, deposit-secured and card-captured.',
    types: [
      { key: 'booking_created', label: 'New booking', desc: 'A client books online or a booking is added' },
      { key: 'booking_rescheduled', label: 'Booking rescheduled', desc: 'A client moves their booking to a new time' },
      { key: 'booking_cancelled', label: 'Booking cancelled', desc: 'A booking is cancelled by the client or in the diary' },
      { key: 'booking_no_show', label: 'No show', desc: 'A booking is marked as a no-show' },
    ],
  },
  {
    title: 'Payments',
    types: [
      { key: 'payment_captured', label: 'Payment taken', desc: 'A payment is captured for a booking' },
      { key: 'deposit_received', label: 'Deposit received', desc: 'A client pays a deposit when booking' },
      { key: 'tip_received', label: 'Tip received', desc: 'A tip comes in with a payment' },
    ],
  },
];

const selectStyles = {
  control: (base) => ({ ...base, minHeight: 34, fontSize: 13, borderRadius: 8, borderColor: '#e0e0e0' }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: (base) => ({ ...base, fontSize: 13, zIndex: 20 }),
};

const Toggle = ({ on, onClick, disabled }) => (
  <button
    type="button"
    className={`ds-toggle${on ? ' ds-toggle--on' : ''}`}
    onClick={onClick}
    disabled={disabled}
  >
    <span className="ds-toggle-knob" />
  </button>
);

const NotificationSettings = (props) => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);

  const role = getNotificationRole(props.user);

  useEffect(() => {
    fetchNotificationPreferences()
      .then((data) => { setPrefs(data); setLoading(false); })
      .catch(() => { setLoading(false); toast.error('Could not load notification settings'); });
  }, []);

  // Debounced autosave of a partial patch; optimistic local state.
  const apply = useCallback((patch, localNext) => {
    setPrefs((prev) => ({ ...prev, ...localNext }));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      updateNotificationPreferences(patch).catch((e) => {
        const msg = e?.response?.data?.messages || e?.response?.data?.error?.responseMessage;
        toast.error(msg ? `Could not save: ${msg}` : 'Could not save. Please try again.');
      });
    }, 400);
  }, []);

  const modeFor = (typeKey) => {
    if (prefs?.types?.[typeKey]) return prefs.types[typeKey];
    return prefs?.resolvedTypes?.[typeKey] || 'realtime';
  };

  const setMode = (typeKey, mode) => {
    const types = { ...(prefs?.types || {}), [typeKey]: mode };
    apply({ types }, { types });
  };

  // booking_created / booking_cancelled emails historically always sent —
  // the server treats an unset preference as ON for those (emailDefaultOn).
  const emailOn = (typeKey) => {
    const v = prefs?.emailTypes?.[typeKey];
    if (v === true) return true;
    if (v === false) return false;
    return (prefs?.emailDefaultOn || []).indexOf(typeKey) !== -1;
  };

  const setEmail = (typeKey, on) => {
    const emailTypes = { ...(prefs?.emailTypes || {}), [typeKey]: on };
    apply({ emailTypes }, { emailTypes });
  };

  const setScope = (key, on) => {
    const staffScope = { ...(prefs?.staffScope || {}), [key]: on };
    apply({ staffScope }, { staffScope });
  };

  return (
    <SettingsPageLayout activeTab="notifications" {...props}>
      {loading || !prefs ? (
        <div className="settings-card"><p className="bu-hint">Loading…</p></div>
      ) : (
        <>
          <div className="settings-card">
            <div className="settings-card-header">
              <h2>Notifications</h2>
              <p>What appears in your notification bell, and what gets emailed to you.</p>
            </div>
            <div className="bu-toggle-row">
              <div className="bu-toggle-text">
                <span>All notifications</span>
                <span className="bu-hint">Master switch. Off means nothing is written or sent</span>
              </div>
              <Toggle
                on={prefs.enabled !== false}
                onClick={() => apply({ enabled: !(prefs.enabled !== false) }, { enabled: !(prefs.enabled !== false) })}
              />
            </div>
          </div>

          {prefs.enabled !== false && TYPE_GROUPS.map((group) => (
            <div className="settings-card" key={group.title}>
              <div className="settings-card-header">
                <h2>{group.title}</h2>
                {group.hint && <p>{group.hint}</p>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 26, padding: '0 2px 4px', fontSize: 12, color: '#98A2B3' }}>
                <span style={{ width: 140, textAlign: 'left' }}>In Beu</span>
                <span style={{ width: 44 }}>Email</span>
              </div>
              {group.types.map((t) => (
                <div className="bu-toggle-row" key={t.key}>
                  <div className="bu-toggle-text">
                    <span>{t.label}</span>
                    <span className="bu-hint">{t.desc}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 26, flexShrink: 0 }}>
                    <div style={{ width: 140 }}>
                      <Select
                        options={MODE_OPTIONS}
                        value={MODE_OPTIONS.find((o) => o.value === modeFor(t.key))}
                        styles={selectStyles}
                        isSearchable={false}
                        onChange={(o) => setMode(t.key, o.value)}
                      />
                    </div>
                    <div style={{ width: 44 }}>
                      <Toggle
                        on={emailOn(t.key)}
                        onClick={() => setEmail(t.key, !emailOn(t.key))}
                        disabled={modeFor(t.key) === 'off'}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}

          {role === 'manager' && prefs.enabled !== false && (
            <div className="settings-card">
              <div className="settings-card-header">
                <h2>Staff activity</h2>
                <p>Whose booking activity reaches you. Your own bookings always do.</p>
              </div>
              <div className="bu-toggle-row">
                <div className="bu-toggle-text">
                  <span>Employed staff</span>
                  <span className="bu-hint">Bookings, cancellations and reschedules for employed team members</span>
                </div>
                <Toggle
                  on={prefs.staffScope?.employed !== false}
                  onClick={() => setScope('employed', !(prefs.staffScope?.employed !== false))}
                />
              </div>
              <div className="bu-toggle-row">
                <div className="bu-toggle-text">
                  <span>Self-employed staff</span>
                  <span className="bu-hint">Only for self-employed members who have chosen to share their activity with you</span>
                </div>
                <Toggle
                  on={prefs.staffScope?.selfEmployed !== false}
                  onClick={() => setScope('selfEmployed', !(prefs.staffScope?.selfEmployed !== false))}
                />
              </div>
            </div>
          )}

          {role === 'self_employed' && prefs.enabled !== false && (
            <div className="settings-card">
              <div className="settings-card-header">
                <h2>Share with your salon</h2>
                <p>Your bookings are your business. Sharing is your call.</p>
              </div>
              <div className="bu-toggle-row">
                <div className="bu-toggle-text">
                  <span>Share my booking activity</span>
                  <span className="bu-hint">The salon manager gets notified about your bookings, cancellations and reschedules</span>
                </div>
                <Toggle
                  on={prefs.shareWithSalon === true}
                  onClick={() => apply({ shareWithSalon: !(prefs.shareWithSalon === true) }, { shareWithSalon: !(prefs.shareWithSalon === true) })}
                />
              </div>
            </div>
          )}
        </>
      )}
    </SettingsPageLayout>
  );
};

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps)(NotificationSettings);
